import type { DeepPartial } from '@givebacks/utils/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { studentKeys } from '../../../student/query-key-factory';
import { StoreApi } from '../../api';
import { storeKeys } from '../../query-key-factory';
import type { Checkout, ConsumerCheckoutCartsResponse } from './checkout.types';

const useUpdateConsumerCheckout = () => {
  const queryClient = useQueryClient();

  const updateCheckout = async ({
    checkoutId,
    cartIds,
    checkout,
  }: {
    checkoutId: string;
    cartIds: string[];
    checkout: DeepPartial<Checkout>;
  }) => {
    const data = await StoreApi.put(`/consumer/checkouts/${checkoutId}`, {
      checkout: {
        ...checkout,
      },
      cart_ids: cartIds,
    });
    return data;
  };

  return useMutation({
    mutationFn: updateCheckout,
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: storeKeys.id('user').carts.all });
      void queryClient.invalidateQueries({
        queryKey: storeKeys.id('user').consumer.checkout.all,
      });
    },
    mutationKey: ['checkout', 'update'],
  });
};

/** Updates the checkout rewards but also ensures that the checkout is invalidated while pending */
const useUpdateConsumerCheckoutRewards = () => {
  const queryClient = useQueryClient();

  const updateCheckout = async ({
    checkoutId,
    cartIds,
    checkout,
  }: {
    checkoutId: string;
    cartIds: string[];
    checkout: DeepPartial<Checkout>;
  }) => {
    const { data } = await StoreApi.put<ConsumerCheckoutCartsResponse>(
      `/consumer/checkouts/${checkoutId}`,
      {
        checkout: {
          ...checkout,
        },
        cart_ids: cartIds,
      }
    );
    return data;
  };

  return useMutation({
    mutationFn: updateCheckout,
    async onSuccess() {
      void queryClient.invalidateQueries({ queryKey: storeKeys.id('user').carts.all });
      await queryClient.invalidateQueries({
        queryKey: storeKeys.id('user').consumer.checkout.all,
      });
    },

    mutationKey: ['checkout', 'update'],
  });
};

const addDiscountToCheckout = async ({
  checkoutId,
  discountCode,
  cartIds,
}: {
  checkoutId: string;
  discountCode: string;
  cartIds: string[];
}) => {
  const data = await StoreApi.post<ConsumerCheckoutCartsResponse>(
    `/consumer/checkouts/${checkoutId}/discounts`,
    {
      discount_code: discountCode,
      cart_ids: cartIds,
    }
  );
  return data;
};

const useAddDiscountToCheckout = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addDiscountToCheckout,
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: storeKeys.id('user').carts.all });
      void queryClient.invalidateQueries({ queryKey: storeKeys.id('user').consumer.checkout.all });
    },
  });
};

const removeDiscountFromCheckout = async ({
  checkoutId,
  discountId,
  cartIds,
}: {
  checkoutId: string;
  discountId: string;
  cartIds: string[];
}) => {
  const data = await StoreApi.delete(`/consumer/checkouts/${checkoutId}/discounts`, {
    params: {
      discount_code: discountId,
      cart_ids: cartIds.join(','),
    },
  });
  return data;
};

const useRemoveDiscountFromCheckout = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeDiscountFromCheckout,
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: storeKeys.id('user').carts.all });
      void queryClient.invalidateQueries({ queryKey: storeKeys.id('user').consumer.checkout.all });
    },
  });
};

type CompleteCheckoutRequest = {
  checkoutId: string;
  cartIds: string[];
  checkout: DeepPartial<Checkout>;
  /** If a cart totals $0, a token is not required when a cart is created at checkout */
  token?: string;
  recaptcha: string | null;
  cvv_token?: string;
};

const useCompleteCheckout = () => {
  const queryClient = useQueryClient();
  const checkoutCart = async ({
    checkoutId,
    cartIds,
    checkout,
    token,
    recaptcha,
    cvv_token,
  }: CompleteCheckoutRequest) => {
    const { data } = await StoreApi.post<ConsumerCheckoutCartsResponse>(
      `/consumer/checkouts/${checkoutId}/checkout`,
      {
        cart_ids: cartIds,
        checkout: {
          ...checkout,
          cvv_token,
          metadata: {
            ...(checkout.metadata || {}),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
          internal_token: token,
        },
        recaptcha_key: recaptcha,
      }
    );

    return data;
  };
  return useMutation({
    mutationFn: checkoutCart,
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: storeKeys.id('user').all });
      void queryClient.invalidateQueries({ queryKey: studentKeys.all });
    },
  });
};
export {
  useAddDiscountToCheckout,
  useCompleteCheckout,
  useRemoveDiscountFromCheckout,
  useUpdateConsumerCheckout,
  useUpdateConsumerCheckoutRewards,
};
